import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AA } from "@/constants/testIds";
import TurnstileWidget, { TURNSTILE_ENABLED } from "@/components/site/TurnstileWidget";
import { Crosshair, Calculator, CheckCircle, ArrowRight, X } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export default function QuoteGenerator() {
  const [services, setServices] = useState([]);
  const [svcKey, setSvcKey] = useState("");
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [estimate, setEstimate] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    project_details: "",
  });

  useEffect(() => {
    axios
      .get(`${API}/services`)
      .then((r) => {
        setServices(r.data);
        if (r.data[0]) setSvcKey(r.data[0].key);
      })
      .catch(() => setError("Could not load services. Refresh and try again."));
  }, []);

  const selectedSvc = useMemo(
    () => services.find((s) => s.key === svcKey),
    [services, svcKey]
  );

  const submitEstimate = async (e) => {
    e?.preventDefault?.();
    setError("");
    setEstimate(null);
    setSubmitted(false);
    if (!svcKey || !address.trim()) {
      setError("Pick a service and enter a project address.");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/quote/estimate`, {
        service: svcKey,
        address: address.trim(),
      });
      setEstimate(r.data);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Could not generate estimate. Try a more specific address.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      setError("Name, email and phone are required.");
      return;
    }
    if (TURNSTILE_ENABLED && !turnstileToken) {
      setError("Please complete the captcha verification.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/quote/request`, {
        ...form,
        service: svcKey,
        address,
        estimated_total_low: estimate?.estimated_total_low,
        estimated_total_high: estimate?.estimated_total_high,
        distance_miles: estimate?.distance_miles,
        turnstile_token: turnstileToken,
      });
      setSubmitted(true);
      setShowForm(false);
      setTurnstileToken(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit request. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="quote" className="relative bg-[#121212] py-24 lg:py-32 border-t border-white/5">
      <div className="absolute inset-0 bp-grid-fine opacity-40 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="label-mono mb-5">04 / Instant Estimate</div>
            <h2 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Locate your site.<br />
              <span className="text-[#FF4500]">Get a number in seconds.</span>
            </h2>
            <p className="mt-6 text-white/60 max-w-xl text-[15px] leading-relaxed">
              Pick a service, drop your project address, and our quote engine geocodes it,
              measures the great-circle distance from our Philadelphia hub, and returns a
              live budget range. Ready to move? Convert it to a real proposal.
            </p>
          </div>
          <div className="lg:col-span-5 self-end">
            <div className="hairline corner-ticks p-5 relative bg-[#0A0A0A]">
              <span className="ct-tr" /><span className="ct-bl" />
              <div className="label-mono mb-3">Service Hub / Origin</div>
              <div className="font-display font-black text-3xl text-white leading-tight">
                Philadelphia, PA
              </div>
              <div className="font-mono text-[12px] text-white/50 mt-1">
                39.9526°N · 75.1652°W · NAVD88
              </div>
              <div className="font-mono text-[10px] text-white/40 mt-3 tracking-[0.18em] uppercase">
                Coverage radius: 600 mi · VA → ME
              </div>
            </div>
          </div>
        </div>

        {/* Generator panel */}
        <div className="grid lg:grid-cols-12 gap-0 hairline">
          {/* Form */}
          <form
            onSubmit={submitEstimate}
            className="lg:col-span-7 p-7 lg:p-10 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0A0A0A]"
          >
            <div className="space-y-7">
              <div>
                <label className="label-mono block mb-2">A · Service</label>
                <select
                  data-testid={AA.quoteServiceSelect}
                  value={svcKey}
                  onChange={(e) => setSvcKey(e.target.value)}
                  className="aa-input font-mono text-sm"
                >
                  {services.map((s) => (
                    <option key={s.key} value={s.key} style={{ background: "#0A0A0A" }}>
                      {`${s.label} · base ${money(s.base_price)} ${s.unit}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-mono block mb-2">B · Project Address</label>
                <input
                  data-testid={AA.quoteAddressInput}
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="e.g. 350 5th Ave, New York, NY"
                  className="aa-input"
                />
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/40 mt-2">
                  Include city & state for best geocoding accuracy
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                <button
                  type="submit"
                  data-testid={AA.quoteEstimateBtn}
                  disabled={loading}
                  className="aa-btn aa-btn-primary btn-traced disabled:opacity-60"
                >
                  {loading ? "Calculating…" : (
                    <>
                      <Calculator size={16} weight="bold" /> Calculate Estimate
                    </>
                  )}
                </button>
                {error && (
                  <span className="font-mono text-xs text-[#FF4500] tracking-wider">
                    ⚠ {error}
                  </span>
                )}
              </div>
            </div>
          </form>

          {/* Result */}
          <div className="lg:col-span-5 p-7 lg:p-10 bg-[#0F0F0F] relative" data-testid={AA.quoteResult}>
            <div className="label-mono mb-4 flex items-center gap-2">
              <Crosshair size={12} weight="bold" /> Live Estimate Readout
            </div>

            {!estimate && !loading && (
              <div className="text-white/40 font-mono text-sm h-full flex items-center min-h-[280px]">
                <div>
                  <div className="text-white/70 text-base mb-2 font-display">Awaiting target…</div>
                  Pick a service & enter an address — we&apos;ll triangulate from the hub and
                  return your number here.
                </div>
              </div>
            )}

            {loading && (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-6 shimmer" />
                ))}
              </div>
            )}

            {estimate && !loading && (
              <div>
                <div className="font-display font-bold text-white text-xl mb-1">
                  {estimate.service_label}
                </div>
                <div className="font-mono text-[11px] text-white/50 mb-5 truncate">
                  {estimate.address_resolved}
                </div>

                <div className="space-y-3 mb-5">
                  <Row k="Distance from hub" v={`${estimate.distance_miles} mi`} />
                  <Row k="Base price" v={`${money(estimate.base_price)} ${estimate.unit}`} />
                  <Row k="Travel fee" v={money(estimate.travel_fee)} />
                  <Row
                    k="Coverage"
                    v={
                      estimate.in_service_area ? (
                        <span className="text-[#FF4500]">In service area ✓</span>
                      ) : (
                        <span className="text-[#FFD700]">Out of standard area — contact us</span>
                      )
                    }
                  />
                </div>

                <div className="hairline corner-ticks bg-[#0A0A0A] p-5 relative">
                  <span className="ct-tr" /><span className="ct-bl" />
                  <div className="label-mono mb-2">Estimated total range</div>
                  <div className="font-display font-black text-3xl sm:text-4xl text-white leading-none">
                    {money(estimate.estimated_total_low)}
                    <span className="text-white/40"> – </span>
                    {money(estimate.estimated_total_high)}
                  </div>
                  <div className="font-mono text-[11px] text-white/50 mt-2">
                    Non-binding · final scope sets the proposal
                  </div>
                </div>

                <button
                  data-testid={AA.quoteRequestBtn}
                  onClick={() => setShowForm(true)}
                  className="aa-btn aa-btn-primary w-full justify-center mt-5"
                >
                  Convert to Proposal <ArrowRight size={16} weight="bold" />
                </button>
              </div>
            )}

            {submitted && (
              <div
                data-testid={AA.quoteSuccess}
                className="mt-6 hairline bg-[#0A0A0A] p-5 flex items-start gap-3"
              >
                <CheckCircle size={22} weight="duotone" className="text-[#FF4500] mt-0.5" />
                <div>
                  <div className="font-display font-bold text-white">Request received.</div>
                  <div className="font-mono text-[12px] text-white/60 mt-1">
                    A surveyor will be in touch within one business day.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showForm && (
        <RequestModal
          form={form}
          setForm={setForm}
          onClose={() => setShowForm(false)}
          onSubmit={submitRequest}
          submitting={submitting}
          serviceLabel={selectedSvc?.label}
          estimate={estimate}
          error={error}
          onTurnstile={setTurnstileToken}
        />
      )}
    </section>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-white/5 pb-2">
      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-white/45">{k}</span>
      <span className="font-display font-bold text-white text-[15px]">{v}</span>
    </div>
  );
}

function RequestModal({ form, setForm, onClose, onSubmit, submitting, serviceLabel, estimate, error, onTurnstile }) {
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="relative w-full sm:max-w-2xl bg-[#0A0A0A] hairline corner-ticks p-7 sm:p-10 max-h-[90vh] overflow-y-auto">
        <span className="ct-tr" /><span className="ct-bl" />
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-[#FF4500]"
          aria-label="Close"
        >
          <X size={22} weight="bold" />
        </button>
        <div className="label-mono mb-3">Convert estimate · {serviceLabel}</div>
        <h3 className="font-display font-black text-white text-3xl tracking-tighter mb-1">
          Send me a proposal.
        </h3>
        {estimate && (
          <div className="font-mono text-[12px] text-white/50 mb-6">
            Range {Number(estimate.estimated_total_low).toLocaleString()}
            – {Number(estimate.estimated_total_high).toLocaleString()} USD · {estimate.distance_miles} mi
          </div>
        )}
        <form onSubmit={onSubmit} className="grid sm:grid-cols-2 gap-4">
          <Field label="Name" required>
            <input data-testid={AA.quoteForm.name} className="aa-input" value={form.name} onChange={upd("name")} required />
          </Field>
          <Field label="Phone" required>
            <input data-testid={AA.quoteForm.phone} className="aa-input" value={form.phone} onChange={upd("phone")} required />
          </Field>
          <Field label="Email" required>
            <input data-testid={AA.quoteForm.email} type="email" className="aa-input" value={form.email} onChange={upd("email")} required />
          </Field>
          <Field label="Company">
            <input data-testid={AA.quoteForm.company} className="aa-input" value={form.company} onChange={upd("company")} />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Project details">
              <textarea
                data-testid={AA.quoteForm.details}
                rows={4}
                className="aa-input resize-none"
                placeholder="Schedule, scope, contractor, datum reference, etc."
                value={form.project_details}
                onChange={upd("project_details")}
              />
            </Field>
          </div>
          {error && (
            <div className="sm:col-span-2 font-mono text-xs text-[#FF4500]">{error}</div>
          )}
          {TURNSTILE_ENABLED && (
            <div className="sm:col-span-2">
              <div className="label-mono mb-2">Verify you&apos;re human</div>
              <TurnstileWidget onToken={onTurnstile} />
            </div>
          )}
          <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center mt-2">
            <button
              type="submit"
              data-testid={AA.quoteForm.submit}
              disabled={submitting}
              className="aa-btn aa-btn-primary btn-traced disabled:opacity-60"
            >
              {submitting ? "Submitting…" : "Submit Request"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="aa-btn aa-btn-ghost"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="label-mono mb-2">
        {label} {required && <span className="text-[#FF4500]">*</span>}
      </div>
      {children}
    </label>
  );
}
